import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Users, Calendar, FileText, MessageSquare, Bell, 
  HandHeart, Search, Trash2, Download, Plus, Mail,
  Check, X, Eye, Edit, Pin
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const tagLabels = {
  autistic: 'Autistic',
  parent: 'Parent',
  teacher: 'Teacher',
  employer: 'Employer',
  volunteer: 'Volunteer',
  suspect_autistic: 'Exploring'
};

export default function AdminPanel() {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('members');
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [members, setMembers] = useState([]);
  const [events, setEvents] = useState([]);
  const [posts, setPosts] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [volunteerApps, setVolunteerApps] = useState([]);
  const [trainingInquiries, setTrainingInquiries] = useState([]);
  
  // Filter states
  const [memberFilter, setMemberFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialog states
  const [newAnnouncementOpen, setNewAnnouncementOpen] = useState(false);
  const [newAnnouncement, setNewAnnouncement] = useState({ title: '', content: '', priority: 'normal' });
  const [newEventOpen, setNewEventOpen] = useState(false);
  const [newEvent, setNewEvent] = useState({
    title: '', description: '', event_type: 'webinar', date: '', time: '', end_time: '', location: '', virtual_link: ''
  });
  
  // Edit states
  const [editEventOpen, setEditEventOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [viewPostOpen, setViewPostOpen] = useState(false);
  const [viewingPost, setViewingPost] = useState(null);
  const [editPostOpen, setEditPostOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      return;
    }
    fetchData();
  }, [isAdmin, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [membersRes, eventsRes, postsRes, announcementsRes, volunteerRes, trainingRes] = await Promise.all([
        axios.get(`${API}/admin/members`),
        axios.get(`${API}/events?upcoming=false`),
        axios.get(`${API}/forum/posts`),
        axios.get(`${API}/announcements`),
        axios.get(`${API}/volunteer/applications`),
        axios.get(`${API}/training/inquiries`)
      ]);
      
      setMembers(membersRes.data);
      setEvents(eventsRes.data);
      setPosts(postsRes.data);
      setAnnouncements(announcementsRes.data);
      setVolunteerApps(volunteerRes.data);
      setTrainingInquiries(trainingRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const filteredMembers = members.filter(m => {
    const matchesSearch = 
      m.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (memberFilter === 'all') return matchesSearch;
    if (memberFilter === 'newsletter') return matchesSearch && m.newsletter;
    return matchesSearch && m.tags?.[memberFilter];
  });

  const handleDeleteMember = async (memberId) => {
    if (!window.confirm('Are you sure you want to remove this member?')) return;
    try {
      await axios.delete(`${API}/admin/members/${memberId}`);
      setMembers(members.filter(m => m.id !== memberId));
      toast.success('Member removed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove member');
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    try {
      await axios.delete(`${API}/forum/posts/${postId}`);
      setPosts(posts.filter(p => p.id !== postId));
      toast.success('Post deleted');
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const handleCreateAnnouncement = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/announcements`, newAnnouncement);
      setAnnouncements([response.data, ...announcements]);
      setNewAnnouncementOpen(false);
      setNewAnnouncement({ title: '', content: '', priority: 'normal' });
      toast.success('Announcement posted!');
    } catch (error) {
      toast.error('Failed to create announcement');
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/events`, newEvent);
      setEvents([response.data, ...events]);
      setNewEventOpen(false);
      setNewEvent({ title: '', description: '', event_type: 'webinar', date: '', time: '', end_time: '', location: '', virtual_link: '' });
      toast.success('Event created!');
    } catch (error) {
      toast.error('Failed to create event');
    }
  };

  const handleEditEvent = (event) => {
    setEditingEvent({
      ...event,
      date: event.date?.split('T')[0] || event.date || ''
    });
    setEditEventOpen(true);
  };

  const handleUpdateEvent = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/events/${editingEvent.id}`, editingEvent);
      setEvents(events.map(ev => ev.id === editingEvent.id ? editingEvent : ev));
      setEditEventOpen(false);
      setEditingEvent(null);
      toast.success('Event updated!');
    } catch (error) {
      toast.error('Failed to update event');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    try {
      await axios.delete(`${API}/events/${eventId}`);
      setEvents(events.filter(e => e.id !== eventId));
      toast.success('Event deleted');
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const handleViewPost = (post) => {
    setViewingPost(post);
    setViewPostOpen(true);
  };

  const handleEditPost = (post) => {
    setEditingPost({ ...post });
    setEditPostOpen(true);
  };

  const handleUpdatePost = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/forum/posts/${editingPost.id}`, {
        title: editingPost.title,
        content: editingPost.content
      });
      setPosts(posts.map(p => p.id === editingPost.id ? { ...p, title: editingPost.title, content: editingPost.content } : p));
      setEditPostOpen(false);
      setEditingPost(null);
      toast.success('Post updated!');
    } catch (error) {
      toast.error('Failed to update post');
    }
  };

  const handleTogglePin = async (post) => {
    try {
      await axios.post(`${API}/forum/posts/${post.id}/pin`);
      setPosts(posts.map(p => p.id === post.id ? { ...p, pinned: !p.pinned } : p));
      toast.success(post.pinned ? 'Post unpinned' : 'Post pinned');
    } catch (error) {
      toast.error('Failed to toggle pin');
    }
  };

  const handleUpdateVolunteerStatus = async (appId, status) => {
    try {
      await axios.patch(`${API}/volunteer/applications/${appId}?status=${status}`);
      setVolunteerApps(volunteerApps.map(a => 
        a.id === appId ? { ...a, status } : a
      ));
      toast.success('Status updated');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const exportMembers = () => {
    const csv = [
      ['First Name', 'Last Name', 'Email', 'Phone', 'Tags', 'Newsletter', 'Joined'].join(','),
      ...filteredMembers.map(m => [
        m.first_name,
        m.last_name,
        m.email,
        m.phone,
        Object.entries(m.tags || {}).filter(([,v]) => v).map(([k]) => tagLabels[k]).join(';'),
        m.newsletter ? 'Yes' : 'No',
        m.created_at ? format(new Date(m.created_at), 'yyyy-MM-dd') : ''
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `members-${memberFilter}-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
  };

  if (!isAdmin) return null;

  return (
    <div className="min-h-screen py-8">
      <div className="container-main">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold">Admin Panel</h1>
            <p className="text-muted-foreground">Manage members, content, and events</p>
          </div>
          <Badge className="bg-primary">{user?.role}</Badge>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-3 md:grid-cols-6 gap-2 h-auto bg-transparent">
            <TabsTrigger value="members" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Users className="h-4 w-4 mr-2" />
              Members
            </TabsTrigger>
            <TabsTrigger value="events" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Calendar className="h-4 w-4 mr-2" />
              Events
            </TabsTrigger>
            <TabsTrigger value="forum" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <MessageSquare className="h-4 w-4 mr-2" />
              Forum
            </TabsTrigger>
            <TabsTrigger value="announcements" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Bell className="h-4 w-4 mr-2" />
              Announcements
            </TabsTrigger>
            <TabsTrigger value="volunteers" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <HandHeart className="h-4 w-4 mr-2" />
              Volunteers
            </TabsTrigger>
            <TabsTrigger value="training" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <FileText className="h-4 w-4 mr-2" />
              Training
            </TabsTrigger>
          </TabsList>

          {/* Members Tab */}
          <TabsContent value="members">
            <Card>
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <CardTitle>Members ({filteredMembers.length})</CardTitle>
                    <CardDescription>Manage hub members and subscriptions</CardDescription>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search members..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 w-64"
                      />
                    </div>
                    <Select value={memberFilter} onValueChange={setMemberFilter}>
                      <SelectTrigger className="w-40">
                        <SelectValue placeholder="Filter by tag" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Members</SelectItem>
                        <SelectItem value="newsletter">Newsletter</SelectItem>
                        <SelectItem value="autistic">Autistic</SelectItem>
                        <SelectItem value="parent">Parents</SelectItem>
                        <SelectItem value="teacher">Teachers</SelectItem>
                        <SelectItem value="employer">Employers</SelectItem>
                        <SelectItem value="volunteer">Volunteers</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button variant="outline" onClick={exportMembers}>
                      <Download className="h-4 w-4 mr-2" />
                      Export
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Tags</TableHead>
                        <TableHead>Newsletter</TableHead>
                        <TableHead>Joined</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredMembers.slice(0, 50).map((member) => (
                        <TableRow key={member.id}>
                          <TableCell className="font-medium">
                            {member.first_name} {member.last_name}
                            {member.role !== 'member' && (
                              <Badge variant="outline" className="ml-2">{member.role}</Badge>
                            )}
                          </TableCell>
                          <TableCell>{member.email}</TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {Object.entries(member.tags || {}).filter(([,v]) => v).map(([key]) => (
                                <Badge key={key} variant="secondary" className="text-xs">
                                  {tagLabels[key]}
                                </Badge>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell>
                            {member.newsletter ? (
                              <Check className="h-4 w-4 text-green-500" />
                            ) : (
                              <X className="h-4 w-4 text-muted-foreground" />
                            )}
                          </TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {member.created_at ? format(new Date(member.created_at), 'MMM d, yyyy') : '-'}
                          </TableCell>
                          <TableCell>
                            {member.role === 'member' && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDeleteMember(member.id)}
                                className="text-destructive"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Events Tab */}
          <TabsContent value="events">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Events</CardTitle>
                    <CardDescription>Manage events and registrations</CardDescription>
                  </div>
                  <Dialog open={newEventOpen} onOpenChange={setNewEventOpen}>
                    <DialogTrigger asChild>
                      <Button className="btn-primary">
                        <Plus className="h-4 w-4 mr-2" />
                        New Event
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Create New Event</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleCreateEvent} className="space-y-4">
                        <div className="space-y-2">
                          <Label>Title</Label>
                          <Input
                            value={newEvent.title}
                            onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Description</Label>
                          <Textarea
                            value={newEvent.description}
                            onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                            required
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Type</Label>
                            <Select
                              value={newEvent.event_type}
                              onValueChange={(v) => setNewEvent({ ...newEvent, event_type: v })}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="webinar">Webinar</SelectItem>
                                <SelectItem value="family_fun_day">Family Fun Day</SelectItem>
                                <SelectItem value="workshop">Workshop</SelectItem>
                                <SelectItem value="meetup">Meetup</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label>Date</Label>
                            <Input
                              type="date"
                              value={newEvent.date}
                              onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
                              required
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Start Time</Label>
                            <Input
                              type="time"
                              value={newEvent.time}
                              onChange={(e) => setNewEvent({ ...newEvent, time: e.target.value })}
                              required
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>End Time</Label>
                            <Input
                              type="time"
                              value={newEvent.end_time}
                              onChange={(e) => setNewEvent({ ...newEvent, end_time: e.target.value })}
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Location (optional)</Label>
                          <Input
                            value={newEvent.location}
                            onChange={(e) => setNewEvent({ ...newEvent, location: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Virtual Link (optional)</Label>
                          <Input
                            value={newEvent.virtual_link}
                            onChange={(e) => setNewEvent({ ...newEvent, virtual_link: e.target.value })}
                          />
                        </div>
                        <DialogFooter>
                          <Button type="submit">Create Event</Button>
                        </DialogFooter>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Event</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Registrations</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {events.map((event) => (
                      <TableRow key={event.id}>
                        <TableCell className="font-medium">{event.title}</TableCell>
                        <TableCell>{event.date} at {event.time}{event.end_time ? ` - ${event.end_time}` : ''}</TableCell>
                        <TableCell>{event.attendees_count || 0}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" onClick={() => handleEditEvent(event)} title="Edit">
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDeleteEvent(event.id)} className="text-destructive" title="Delete">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Forum Tab */}
          <TabsContent value="forum">
            <Card>
              <CardHeader>
                <CardTitle>Forum Posts</CardTitle>
                <CardDescription>Moderate community discussions</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Title</TableHead>
                      <TableHead>Author</TableHead>
                      <TableHead>Hearts</TableHead>
                      <TableHead>Comments</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {posts.map((post) => (
                      <TableRow key={post.id}>
                        <TableCell className="font-medium">
                          {post.pinned && <Badge className="mr-2 bg-primary">Pinned</Badge>}
                          {post.title}
                        </TableCell>
                        <TableCell>{post.author_name}</TableCell>
                        <TableCell>{post.hearts}</TableCell>
                        <TableCell>{post.comments_count}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" onClick={() => handleViewPost(post)} title="View">
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleEditPost(post)} title="Edit">
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleTogglePin(post)} title={post.pinned ? "Unpin" : "Pin"} className={post.pinned ? "text-primary" : ""}>
                              <Pin className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDeletePost(post.id)} className="text-destructive" title="Delete">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Announcements Tab */}
          <TabsContent value="announcements">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Announcements</CardTitle>
                    <CardDescription>Post updates to all members</CardDescription>
                  </div>
                  <Dialog open={newAnnouncementOpen} onOpenChange={setNewAnnouncementOpen}>
                    <DialogTrigger asChild>
                      <Button className="btn-primary">
                        <Plus className="h-4 w-4 mr-2" />
                        New Announcement
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Create Announcement</DialogTitle>
                        <DialogDescription>
                          This will be visible to all members and sent as a notification.
                        </DialogDescription>
                      </DialogHeader>
                      <form onSubmit={handleCreateAnnouncement} className="space-y-4">
                        <div className="space-y-2">
                          <Label>Title</Label>
                          <Input
                            value={newAnnouncement.title}
                            onChange={(e) => setNewAnnouncement({ ...newAnnouncement, title: e.target.value })}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Content</Label>
                          <Textarea
                            value={newAnnouncement.content}
                            onChange={(e) => setNewAnnouncement({ ...newAnnouncement, content: e.target.value })}
                            rows={5}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Priority</Label>
                          <Select
                            value={newAnnouncement.priority}
                            onValueChange={(v) => setNewAnnouncement({ ...newAnnouncement, priority: v })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="normal">Normal</SelectItem>
                              <SelectItem value="high">High</SelectItem>
                              <SelectItem value="urgent">Urgent</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <DialogFooter>
                          <Button type="submit">Post Announcement</Button>
                        </DialogFooter>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {announcements.map((ann) => (
                    <Card key={ann.id}>
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div>
                            <Badge className={
                              ann.priority === 'urgent' ? 'bg-destructive' :
                              ann.priority === 'high' ? 'bg-accent' : ''
                            }>
                              {ann.priority}
                            </Badge>
                            <CardTitle className="mt-2">{ann.title}</CardTitle>
                            <CardDescription>
                              By {ann.author_name} • {format(new Date(ann.created_at), 'MMM d, yyyy')}
                            </CardDescription>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <p className="text-muted-foreground">{ann.content}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Volunteers Tab */}
          <TabsContent value="volunteers">
            <Card>
              <CardHeader>
                <CardTitle>Volunteer Applications</CardTitle>
                <CardDescription>Review and manage volunteer applications</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Applied</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {volunteerApps.map((app) => (
                      <TableRow key={app.id}>
                        <TableCell className="font-medium">
                          {app.first_name} {app.last_name}
                        </TableCell>
                        <TableCell>{app.email}</TableCell>
                        <TableCell>
                          <Badge className={
                            app.status === 'approved' ? 'bg-green-500' :
                            app.status === 'rejected' ? 'bg-destructive' : ''
                          }>
                            {app.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {app.created_at ? format(new Date(app.created_at), 'MMM d, yyyy') : '-'}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-green-500"
                              onClick={() => handleUpdateVolunteerStatus(app.id, 'approved')}
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive"
                              onClick={() => handleUpdateVolunteerStatus(app.id, 'rejected')}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Training Tab */}
          <TabsContent value="training">
            <Card>
              <CardHeader>
                <CardTitle>Training Inquiries</CardTitle>
                <CardDescription>Manage training requests from organizations</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Organization</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Training Type</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {trainingInquiries.map((inquiry) => (
                      <TableRow key={inquiry.id}>
                        <TableCell className="font-medium">{inquiry.organization}</TableCell>
                        <TableCell>
                          {inquiry.name}<br />
                          <span className="text-muted-foreground text-sm">{inquiry.email}</span>
                        </TableCell>
                        <TableCell>{inquiry.training_type}</TableCell>
                        <TableCell>
                          {inquiry.created_at ? format(new Date(inquiry.created_at), 'MMM d, yyyy') : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Edit Event Dialog */}
        <Dialog open={editEventOpen} onOpenChange={setEditEventOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Edit Event</DialogTitle>
            </DialogHeader>
            {editingEvent && (
              <form onSubmit={handleUpdateEvent} className="space-y-4">
                <div className="space-y-2">
                  <Label>Title</Label>
                  <Input
                    value={editingEvent.title}
                    onChange={(e) => setEditingEvent({ ...editingEvent, title: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={editingEvent.description}
                    onChange={(e) => setEditingEvent({ ...editingEvent, description: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Date</Label>
                    <Input
                      type="date"
                      value={editingEvent.date}
                      onChange={(e) => setEditingEvent({ ...editingEvent, date: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Start Time</Label>
                    <Input
                      type="time"
                      value={editingEvent.time}
                      onChange={(e) => setEditingEvent({ ...editingEvent, time: e.target.value })}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>End Time</Label>
                    <Input
                      type="time"
                      value={editingEvent.end_time || ''}
                      onChange={(e) => setEditingEvent({ ...editingEvent, end_time: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Location</Label>
                    <Input
                      value={editingEvent.location || ''}
                      onChange={(e) => setEditingEvent({ ...editingEvent, location: e.target.value })}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setEditEventOpen(false)}>Cancel</Button>
                  <Button type="submit">Save Changes</Button>
                </DialogFooter>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* View Post Dialog */}
        <Dialog open={viewPostOpen} onOpenChange={setViewPostOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{viewingPost?.title}</DialogTitle>
              <DialogDescription>
                By {viewingPost?.author_name} • {viewingPost?.hearts} hearts • {viewingPost?.comments_count} comments
              </DialogDescription>
            </DialogHeader>
            <div className="prose dark:prose-invert max-w-none">
              <p className="whitespace-pre-wrap">{viewingPost?.content}</p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setViewPostOpen(false)}>Close</Button>
              <Button onClick={() => { setViewPostOpen(false); handleEditPost(viewingPost); }}>Edit Post</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Post Dialog */}
        <Dialog open={editPostOpen} onOpenChange={setEditPostOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Edit Post</DialogTitle>
            </DialogHeader>
            {editingPost && (
              <form onSubmit={handleUpdatePost} className="space-y-4">
                <div className="space-y-2">
                  <Label>Title</Label>
                  <Input
                    value={editingPost.title}
                    onChange={(e) => setEditingPost({ ...editingPost, title: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Content</Label>
                  <Textarea
                    value={editingPost.content}
                    onChange={(e) => setEditingPost({ ...editingPost, content: e.target.value })}
                    rows={6}
                    required
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setEditPostOpen(false)}>Cancel</Button>
                  <Button type="submit">Save Changes</Button>
                </DialogFooter>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
